import {Injectable, OnDestroy} from '@angular/core';
import {map, switchMap, takeUntil} from "rxjs/operators";
import {forkJoin, Observable, of, Subject} from "rxjs";
import {CustomEventsService} from "../../../services/custom-events.service";
import {ConstantProperty} from "../../../_models/constant-property";
import {ComponentType} from "../../../_models/component-type";
import {EventType} from "../../../_models/event-type";
import {ListResponse} from "../../../services/api/response-types";
import {
    COMPONENT_EVENTS_CONFIG, ISelectedColsType
} from "../../../_typing/config/component-events-table";
import {CustomEventsConfig} from "../../../forms/custom-events-form/custom-events-form.component";
import {PivotTileConfig} from "../../../_typing/config/pivot-tile";
import {KeyMap, ModelID} from "../../../_typing/generic-types";
import {EventConfigColumn} from "../../../_typing/config/config-column";
import {Field, FieldMap, Option} from "../../../_typing/query-builder";
import {
    getColIds,
    getComponentTypeIds,
    getEventTypeIds,
    getPropertyIds
} from "../../../tables/custom-events-table/utils";
import {User} from "../../../_models/users";
import {ApiService} from "../../../services/api/api.service";
import {Group} from "../../../_models/group";
import {Account} from "../../../_models/account";

@Injectable()
export class ValidationFieldOptionService implements OnDestroy {
    readonly onDestroy = new Subject<void>();
    config: COMPONENT_EVENTS_CONFIG | CustomEventsConfig | PivotTileConfig;
    allColumns: boolean = true;

    selectedColumns: ISelectedColsType;
    selectedColsDict: KeyMap<EventConfigColumn>;
    fields: FieldMap;

    constantProperties: ConstantProperty[];
    componentTypes: ComponentType[];
    eventTypes: EventType[];

    constructor(public customEventsService: CustomEventsService,
                private api: ApiService) {
    }

    getDefaultColumn(name, type, value) {
        if (this.allColumns || this.selectedColsDict?.[value]) {
            return {[value]: {name, type, value}};
        }
        return {};
    }

    getComponentTypeFields(component_type_id: ModelID, defaultFields: FieldMap, columns: EventConfigColumn[] = []): Observable<FieldMap> {
        const etIds = getEventTypeIds(columns);
        const cpIds = getPropertyIds(columns);

        const $componentTypes = this.customEventsService.getComponentTypeComponentTypes(component_type_id)
            .pipe(map(res => {
                this.componentTypes = res.data;
                return this.fieldsFromComponentType(this.componentTypes);
            }));

        const $eventTypes = this.customEventsService.getComponentTypeEventTypes([component_type_id], etIds)
            .pipe(map(res => {
                this.eventTypes = res.data;
                return this.fieldsFromEventType(this.eventTypes);
            }));

        const $properties = this.customEventsService.getComponentTypeProperties([component_type_id], cpIds)
            .pipe(switchMap(res => {
                this.constantProperties = res.data;
                return this.fieldsFromConstantProperty(this.constantProperties);
            }));

        return forkJoin([$properties, $componentTypes, $eventTypes]).pipe(
            map(([propertiesField, componentTypesField, eventTypesField]) => {
                return {...defaultFields, ...propertiesField, ...componentTypesField, ...eventTypesField};
            })
        );
    }

    getEventTypeFields(event_type_id: ModelID, defaultFields: FieldMap, columns: EventConfigColumn[] = []): Observable<FieldMap> {
        const ctIds: ModelID[] = getComponentTypeIds(columns);
        const cpIds: ModelID[] = getPropertyIds(columns);

        const $componentTypes = this.customEventsService.getEventTypeComponentTypes([event_type_id], ctIds)
            .pipe(map(res => {
                this.componentTypes = res.data;
                return this.fieldsFromComponentType(this.componentTypes);
            }));

        const $properties = this.customEventsService.getConstantPropertiesByEventTypeIds([event_type_id], cpIds)
            .pipe(switchMap((res: ListResponse<ConstantProperty>) => {
                this.constantProperties = res.data;
                return this.fieldsFromConstantProperty(this.constantProperties);
            }));

        return forkJoin([$properties, $componentTypes]).pipe(
            map(([propertiesField, componentTypesField]) => {
                return {...defaultFields, ...propertiesField, ...componentTypesField};
            })
        );
    }

    private getDropdownOptions(dataType: string): Observable<Option[]> {
        switch (dataType) {
            case 'user':
                return this.api.users.searchMany().pipe(
                    takeUntil(this.onDestroy),
                    map(res => res.data.map(user => ({
                        name: user.attributes.name,
                        value: user.id
                    })))
                );
            case 'user_group':
                return this.api.group.searchMany().pipe(
                    takeUntil(this.onDestroy),
                    map(res => res.data.map(group => ({
                        name: group.attributes.name,
                        value: group.id
                    })))
                );
            case 'account':
                return this.api.account.searchMany().pipe(
                    takeUntil(this.onDestroy),
                    map(res => res.data.map(account => ({
                        name: account.attributes.name,
                        value: account.id
                    })))
                );
            default:
                return new Observable<Option[]>(subscriber => subscriber.complete());
        }
    }

    fieldsFromConstantProperty(constantProperties: ConstantProperty[]): Observable<FieldMap> {
        const fields: FieldMap = {};
        const dropdownFetches: Observable<{key: string, field: Field}>[] = [];

        constantProperties.forEach(cp => {
            if (cp.attributes.data_type === 'file') {
                return;
            }

            let type = '';
            let options: Option[] = [];

            switch (cp.attributes.data_type) {
                case 'float':
                    type = 'number';
                    break;
                case 'user':
                case 'user_group':
                case 'account':
                    type = 'dropdown';
                    const key = `[constant_property@${cp.id}]`;
                    dropdownFetches.push(
                        this.getDropdownOptions(cp.attributes.data_type).pipe(
                            map(fetchedOptions => ({
                                key,
                                field: {
                                    name: this.selectedColsDict?.[cp.id]?.title || cp.attributes.name,
                                    type,
                                    value: cp.id,
                                    options: fetchedOptions
                                }
                            }))
                        )
                    );
                    return;
                default:
                    type = cp.attributes.data_type;
            }

            if (cp.attributes.is_drop_down_list) {
                type = 'dropdown';
                options = cp.attributes.json.map(option => ({name: option, value: option}));
            }

            fields[`[constant_property@${cp.id}]`] = {
                name: this.selectedColsDict?.[cp.id]?.title || cp.attributes.name,
                type,
                value: cp.id,
                ...(type === 'dropdown' && {options: options})
            };
        });

        if (dropdownFetches.length === 0) {
            return of(fields);
        }

        return forkJoin(dropdownFetches).pipe(
            map(results => {
                results.forEach(r => fields[r.key] = r.field);
                return fields;
            })
        );
    }

    fieldsFromComponentType(componentTypes: ComponentType[]): FieldMap {
        return componentTypes.reduce((fields, ct) => {
            fields[`[component_type@${ct.id}]`] = {
                name: ct.attributes.name,
                type: 'boolean'
            };
            return fields;
        }, {});
    }

    fieldsFromEventType(eventTypes: EventType[]): FieldMap {
        return eventTypes.reduce((fields, et) => {
            fields[`[event_type@${et.id}]`] = {
                name: et.attributes.name,
                type: 'boolean'
            };
            return fields;
        }, {});
    }

    ngOnDestroy(): void {
        this.onDestroy.next();
        this.onDestroy.complete();
    }
}
